/**
 * Pure geometry for positioning a marker on a rendered image, extracted from
 * LocationDisplay so it can be unit-tested and shared by multi-marker rendering.
 *
 * All source coordinates (`point`) and `photo` dimensions are in the image's
 * ORIGINAL pixel space (as stored on `PhotoLocation`). Scale is always derived
 * from the photo's own recorded dimensions — never the container — so markers
 * sit on the correct depicted pixel regardless of how the image is displayed.
 */

export interface Point {
    x: number;
    y: number;
}

export interface Size {
    width: number;
    height: number;
}

export interface MarkerOffset {
    /** Pixel offset of the marker CENTER from the container's top-left. */
    left: number;
    top: number;
}

/**
 * Position for `object-fit: cover`: the image scales to FILL the container and
 * overflow is cropped, so the rendered image is centered and the marker offset
 * accounts for the cropped, off-screen portion.
 */
export const coverMarkerPosition = (container: Size, photo: Size, point: Point): MarkerOffset | null => {
    if (!container.width || !container.height || !photo.width || !photo.height) {
        return null;
    }
    const scale = Math.max(container.width / photo.width, container.height / photo.height);
    const renderedW = photo.width * scale;
    const renderedH = photo.height * scale;
    const offsetX = (container.width - renderedW) / 2;
    const offsetY = (container.height - renderedH) / 2;
    return {
        left: offsetX + point.x * scale,
        top: offsetY + point.y * scale
    };
};

/**
 * Position for `object-fit: contain` (or an image rendered at its natural aspect
 * ratio): the wrapper is sized to the image, so there is no centering offset and
 * a single uniform scale (rendered width / photo width) maps both axes.
 */
export const containMarkerPosition = (renderedWidth: number, photoWidth: number, point: Point): MarkerOffset | null => {
    if (!renderedWidth || !photoWidth) {
        return null;
    }
    const scale = renderedWidth / photoWidth;
    return {
        left: point.x * scale,
        top: point.y * scale
    };
};

export interface Rect {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

/** How a hover label must move to stay inside its clipping container. */
export interface LabelFit {
    /** Horizontal shift in screen px from the centered position (0 = unchanged). */
    dx: number;
    /** Place the label above the marker instead of below it. */
    above: boolean;
}

/** Minimum distance between a hover label and the container edge, in screen px. */
export const LABEL_EDGE_PADDING = 4;

/**
 * Keeps a marker's hover label inside the clipping container. `below` and
 * `above` are the label's screen rects at its two natural placements (centered
 * under, or over, the marker). Prefers below; flips above only when the bottom
 * is cut off and the top is not. When the label is wider than the container,
 * the left edge wins.
 */
export const fitMarkerLabel = (container: Rect, below: Rect, above: Rect): LabelFit => {
    const flip = below.bottom > container.bottom - LABEL_EDGE_PADDING
        && above.top >= container.top + LABEL_EDGE_PADDING;
    const rect = flip ? above : below;
    let dx = 0;
    if (rect.right > container.right - LABEL_EDGE_PADDING) {
        dx = container.right - LABEL_EDGE_PADDING - rect.right;
    }
    if (rect.left + dx < container.left + LABEL_EDGE_PADDING) {
        dx = container.left + LABEL_EDGE_PADDING - rect.left;
    }
    return { dx, above: flip };
};
